import type { IntlShape } from 'react-intl';

import { isEqual } from 'lodash';

import { Option } from '~/eds/src/types/Option';
import { DataFieldType } from '~/enums/DataFieldType';
import { DataField } from '~/types/Field';
import { PilotId } from '~/types/PilotId';
import { sortStrings } from '~/utils/sortStrings';
import { capitalizeWords } from '~/utils/strings';

import { SearchQuery } from '../../advanced-search/types';
import { MAX_TESTCASE_NUMBER } from '../constants';
import { PromptModelTestCase, TestCaseReviewStatus } from '../types/deprecated';
import { ModelVersion } from '../types/model';
import {
  DEPRECATED_getModelPublishStatusChip,
  getModelOptimizeStateChip,
} from '../utils/ui';
import {
  FieldModel,
  FieldModelConfigState,
  FieldModelOutputType,
} from './types';

export const getFieldModelHeader = ({
  model: { name, libraryModelId, optimizeState, publishStatus },
  intl,
}: {
  model: FieldModel;
  intl: IntlShape;
}) => ({
  chips: [
    optimizeState
      ? getModelOptimizeStateChip(optimizeState, intl)
      : DEPRECATED_getModelPublishStatusChip(publishStatus, intl),
  ],
  icon: libraryModelId
    ? {
        icon: 'library' as const,
        tooltip: intl.formatMessage({
          id: 'ModelHeader.libraryIconTooltip',
          defaultMessage: 'From model library',
          description: 'Tooltip for models from library icon',
        }),
      }
    : undefined,
  title: capitalizeWords(name),
});

export const getModelHeader = ({
  model: { libraryModelId, optimize, publishStatus },
  name,
  intl,
}: {
  model: ModelVersion;
  name: string;
  intl: IntlShape;
}) => ({
  chips: [
    optimize?.state
      ? getModelOptimizeStateChip(optimize.state, intl)
      : DEPRECATED_getModelPublishStatusChip(publishStatus, intl),
  ],
  icon: libraryModelId
    ? {
        icon: 'library' as const,
        tooltip: intl.formatMessage({
          id: 'ModelHeader.libraryIconTooltip',
          defaultMessage: 'From model library',
          description: 'Tooltip for models from library icon',
        }),
      }
    : undefined,
  title: capitalizeWords(name),
});

export const prepareClassificationOptions = (
  config: FieldModelConfigState,
  testCases: PromptModelTestCase[],
  intl: IntlShape,
) => {
  if (!config.fieldClassification) {
    return [];
  }

  const optionsMap = new Map(
    config.fieldClassification.options.map((option) => [option.value, option]),
  );

  const notApplicableValue = 'Not Applicable';
  const notApplicableLabel = intl.formatMessage({
    id: 'fieldsUtils.notApplicable',
    defaultMessage: 'Not Applicable',
    description: 'Label for not applicable option in classification',
  });

  if (
    config.fieldClassification.isOpenEnded &&
    !optionsMap.has(notApplicableValue)
  ) {
    optionsMap.set(notApplicableValue, {
      value: notApplicableValue,
      label: notApplicableLabel,
    });
  } else if (
    !config.fieldClassification.isOpenEnded &&
    config.fieldClassification.defaultOption
  ) {
    optionsMap.set(config.fieldClassification.defaultOption.value, {
      value: config.fieldClassification.defaultOption.value,
      label: config.fieldClassification.defaultOption.label,
    });
  }

  const prepareOption = (value: string) => {
    if (!optionsMap.has(value)) {
      optionsMap.set(value, {
        label: value,
        value,
      });
    }
  };

  if (config.fieldClassification.isOpenEnded) {
    // Add all prompt model and gold value values from test cases to options
    testCases.forEach((testCase) => {
      if (Array.isArray(testCase.modelValue?.value)) {
        testCase.modelValue?.value.forEach((modelValue) =>
          prepareOption(modelValue),
        );
      }
      if (Array.isArray(testCase.goldValue?.value)) {
        testCase.goldValue?.value.forEach((goldValue) =>
          prepareOption(goldValue),
        );
      }
    });
  }

  return Array.from(optionsMap.values());
};

export const isDataFieldUnavailable = (type: DataFieldType): boolean => {
  switch (type) {
    case DataFieldType.ATTACHMENT:
    case DataFieldType.BOOLEAN:
    case DataFieldType.CLAUSE:
      return true;
    case DataFieldType.ADDRESS:
    case DataFieldType.ARRAY_MULTIPLE:
    case DataFieldType.ARRAY_SINGLE:
    case DataFieldType.DATE:
    case DataFieldType.MONETARY:
    case DataFieldType.NUMBER:
    case DataFieldType.STRING:
    case DataFieldType.TEXT_AREA:
      return false;
    default:
      return true;
  }
};

export const testIsClassificationModel = (field: DataField) =>
  field.type === DataFieldType.ARRAY_MULTIPLE ||
  field.type === DataFieldType.ARRAY_SINGLE;

export const testIsMultiClassificationModel = (field: DataField) =>
  field.type === DataFieldType.ARRAY_MULTIPLE;

export const testIsTextExtractionModel = (field: DataField) =>
  field.type === DataFieldType.STRING || field.type === DataFieldType.TEXT_AREA;

export const mapStatusToTestCaseOutcome = (
  status: TestCaseReviewStatus,
): PromptModelTestCase['outcome'] => {
  switch (status) {
    case 'success':
      return 'correct';
    case 'danger':
      return 'incorrect';
    case 'warning':
      return 'partially_correct';
    case 'inactive':
      return 'skipped';
    case null:
    default:
      return 'unknown';
  }
};

export const mapOutputTypeToDataFieldType = (
  output: FieldModelOutputType,
): DataFieldType => {
  switch (output) {
    case 'datetime':
      return DataFieldType.DATE;
    case 'float':
      return DataFieldType.NUMBER;
    case 'text_area':
      return DataFieldType.TEXT_AREA;
    default:
      return DataFieldType.STRING;
  }
};

export const mapDataFieldTypeToOutputType = (
  type: DataFieldType,
): FieldModelOutputType => {
  switch (type) {
    case DataFieldType.ADDRESS:
    case DataFieldType.MONETARY:
      return 'composite';
    case DataFieldType.DATE:
      return 'datetime';
    case DataFieldType.NUMBER:
      return 'float';
    case DataFieldType.TEXT_AREA:
      return 'text_area';
    case DataFieldType.STRING:
    default:
      return 'string';
  }
};

export const mapTestCaseOutcomeToStatus = (
  testCase: PromptModelTestCase,
): TestCaseReviewStatus => {
  if (!testCase) {
    return null;
  }
  const { outcome } = testCase;
  switch (outcome) {
    case 'correct':
      return 'success';
    case 'incorrect':
      return 'danger';
    case 'partially_correct':
      return 'warning';
    case 'skipped':
      return 'inactive';
    case 'unknown':
    default:
      return null;
  }
};

/**
 * Groups data fields by availability (available at the top and unavailable below)
 * and sorts each group alphabetically.
 */
export const sortDataFieldOptions = (
  a: Option<PilotId | string, DataField>,
  b: Option<PilotId | string, DataField>,
): number => {
  if (a.disabled && !b.disabled) {
    return 1;
  } else if (!a.disabled && b.disabled) {
    return -1;
  } else {
    return sortStrings(a.label, b.label);
  }
};

export const testIsConfigDirty = (
  config: FieldModelConfigState,
  originalConfig?: FieldModelConfigState,
  activeQuery?: SearchQuery,
) => {
  const activeConfig = {
    ...config,
    query: activeQuery,
  };

  return originalConfig
    ? !isEqual(originalConfig, activeConfig)
    : Boolean(
        config.field ||
        config.instructions ||
        (activeQuery && activeQuery.length > 0) ||
        config.internal,
      );
};

export const testIsTestCaseNotExecuted = (testCase: PromptModelTestCase) =>
  testCase.state === 'not_started' || testCase.state === 'error';

export const testIsTestCaseEvaluating = (testCase: PromptModelTestCase) =>
  testCase.state === 'not_started' ||
  testCase.state === 'queued' ||
  testCase.state === 'running';

export const getReviewPaginateTailTooltip = ({
  docsInScope,
  pageIndex,
  intl,
  formatNumber,
}: {
  docsInScope: number;
  pageIndex: number;
  intl: IntlShape;
  formatNumber: (number: number) => string;
}) => {
  if (pageIndex === docsInScope) {
    return intl.formatMessage({
      id: 'fieldsUtils.noMoreDocuments',
      defaultMessage: 'There are no more documents to rate.',
      description: 'Message when no more documents are available to rate',
    });
  } else if (pageIndex === MAX_TESTCASE_NUMBER) {
    return intl.formatMessage(
      {
        id: 'fieldsUtils.maxDocumentsReached',
        defaultMessage:
          "Congratulations! You've rated {maxNumber} documents. This is the maximum number of documents you can rate.",
        description:
          'Message when maximum number of documents have been rated. Example: maxNumber = "100"',
      },
      {
        maxNumber: formatNumber(MAX_TESTCASE_NUMBER),
      },
    );
  } else {
  }
};
